import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { getBlogPosts } from '../api';

const CATEGORY_LABELS = { product: 'Product', education: 'Education' };

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

export default function BlogListPage() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    getBlogPosts()
      .then((res) => { if (!cancelled) setPosts(res.data || []); })
      .catch(() => { if (!cancelled) setError('Could not load posts right now.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '40px 20px' }}>
      <Helmet>
        <title>Blog — Algovex</title>
        <meta name="description" content="Guides on backtesting, technical indicators, and building systematic trading strategies with Algovex." />
        <meta property="og:title" content="Algovex Blog" />
        <meta property="og:description" content="Guides on backtesting, technical indicators, and building systematic trading strategies with Algovex." />
        <meta property="og:url" content="https://algovex.ai/blog" />
        <link rel="canonical" href="https://algovex.ai/blog" />
      </Helmet>

      <div style={{ marginBottom: 32 }}>
        <h1 style={{ margin: '0 0 10px', fontSize: 32, fontWeight: 800, color: 'var(--text)' }}>Blog</h1>
        <p style={{ margin: 0, fontSize: 15, color: 'var(--muted)' }}>
          Notes on backtesting, indicators, and building systematic trading strategies.
        </p>
      </div>

      {loading && <div style={{ color: 'var(--muted)', fontSize: 14 }}>Loading posts…</div>}
      {!loading && error && <div style={{ color: 'var(--red)', fontSize: 14 }}>{error}</div>}
      {!loading && !error && posts.length === 0 && (
        <div style={{ color: 'var(--muted)', fontSize: 14 }}>No posts published yet — check back soon.</div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {posts.map((post) => (
          <div
            key={post.slug}
            onClick={() => navigate(`/blog/${post.slug}`)}
            role="link"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/blog/${post.slug}`); }}
            style={{
              border: '1px solid var(--border)', borderRadius: 12, background: 'var(--surface)',
              padding: '20px 22px', cursor: 'pointer',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
              <span style={{
                fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em',
                color: post.category === 'product' ? 'var(--blue)' : 'var(--teal)',
              }}>
                {CATEGORY_LABELS[post.category] || post.category}
              </span>
              <span style={{ fontSize: 12, color: 'var(--muted)' }}>{formatDate(post.published_at)}</span>
            </div>
            <h2 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 700, color: 'var(--text)' }}>{post.title}</h2>
            {post.excerpt && (
              <p style={{ margin: 0, fontSize: 14, color: 'var(--muted)', lineHeight: 1.6 }}>{post.excerpt}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
